<template>
  <div class="trigger-box-wrapper">
    <div class="trigger-config" v-click-away="hideTriggerConfig" @click="showTriggerConfig()">
      <Triggers :conditions="triggerConditions"></Triggers>

      <AdditionalInformationWrapper v-if="showConfig" color="#E6C300" text-color="#212529">
        <template v-slot:content>
          <div class="trigger-box-section">
            <h6 class="title is-6"><strong>Trigger</strong></h6>
            <h6 class="subtitle is-6">Choose an action that will trigger the flow</h6>
            <div class="trigger-options">
              <div
                class="trigger-option"
                v-for="option in availableTriggerTypes"
                :key="option.type"
                :class="{ selected: isTriggerSelected(option.type) }"
                @click="selectTriggerType(option.type)"
                :id="option.type === 'unknown' ? 'unknown-intent-trigger' : undefined"
              >
                <n-tooltip position="bottom" :text="getTooltipForTrigger(option.type)">
                  <span class="icon is-medium trigger-option-icon">
                    <svg v-if="option.icon.startsWith('#')" class="custom-icon">
                      <use xmlns:xlink="http://www.w3.org/1999/xlink" :xlink:href="`${option.icon}`"></use>
                    </svg>
                    <font-awesome-icon
                      v-if="!option.icon.startsWith('#')"
                      :icon="['far', option.icon]"
                      size="2x"
                    ></font-awesome-icon>
                  </span>
                </n-tooltip>

                <div class="has-text-centered is-size-7 trigger-option-text">
                  {{ option.description }}
                </div>
              </div>
            </div>

            <p class="help is-danger" v-if="!triggerValidity.isValid">
              {{ triggerValidity.message }}
            </p>

            <h6 class="subtitle is-6">When these conditions are met</h6>
            <ConditionGroupBuilder
              :value="triggerConditionList"
              :manager="triggerConditionsManager"
              context="condition"
              @updated="triggerConditionsUpdated"
              @config-opened="setConditionOpenedState(true)"
              @config-closed="setConditionOpenedState(false)"
            />
          </div>

          <div class="trigger-box-section">
            <h6 class="title is-6"><strong>Audience</strong></h6>
            <h6 class="subtitle is-6">Select which contacts are sent the flow</h6>
            <ConditionGroupBuilder
              :value="audienceConditionList"
              :manager="audienceConditionsManager"
              context="condition"
              @updated="audienceConditionsUpdated"
              @config-opened="setConditionOpenedState(true)"
              @config-closed="setConditionOpenedState(false)"
            >
              <template v-slot:before>
                <MultiContactTypePicker
                  class="contact-type-picker"
                  :model-value="contactTypes"
                  @updated-type="contactTypeUpdated"
                />
              </template>
            </ConditionGroupBuilder>
          </div>

          <div class="trigger-box-section">
            <h6 class="title is-6"><strong>Schedule</strong></h6>
            <h6 class="subtitle is-6">
              Decide when the flow should interact with your visitors
              <n-tooltip position="top" text="Your default business hours are defined in workspace settings" />
            </h6>
            <TriggerScheduler
              :condition="scheduleCondition"
              :openingTimes="openingTimes"
              @updated="onScheduleUpdated"
            />
          </div>
        </template>
      </AdditionalInformationWrapper>
    </div>

    <div class="invalid-warning" v-if="!triggerValidity.isValid">
      <ErrorIcon class="icon-1-3x"></ErrorIcon>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { AvailableTriggers, TriggerType } from '@/features/bots/models/designer';
import {
  SET_TRIGGER,
  UPDATE_SCHEDULE_CONDITION,
  UPDATE_TRIGGER_CONDITIONS,
  UPDATE_AUDIENCE_CONDITIONS,
  UPDATE_TARGET_CONTACT_TYPES,
} from '@/store/bot-designer/triggers/mutationTypes';
import TriggerScheduler from './TriggerScheduler.vue';
import AdditionalInformationWrapper from './AdditionalInformationWrapper.vue';
import ErrorIcon from 'vue-material-design-icons/AlertCircleOutline.vue';
import Triggers from '@/features/bots/components/designer/Triggers.vue';
import { BusinessHoursViewModel } from '../../models';
import { TriggerValidity } from '@/features/bots/models/designer/triggerValidity';
import { NLP_ENABLED } from '@/store/app/featureTypes';
import { ConditionGroupViewModel } from '@/shared/components/conditions/models/conditionGroupViewModel';
import { ConditionViewModel } from '@/shared/components/conditions/models/conditionViewModel';
import { ConditionGroup } from '@/shared/components/conditions/models/conditionGroup';
import ConditionGroupBuilder, { ConditionGroupUpdate } from '@/shared/components/conditions/ConditionGroupBuilder.vue';
import { ContactType } from '@/features/contacts/models/contactType';
import MultiContactTypePicker from '@/features/bots/components/designer/MultiContactTypePicker.vue';
import { ChannelIdentityViewModel } from '@/messaging/models/channelIdentityViewModel';
import { PROACTIVE_FLOWS_ENABLED } from '@/store/app/featureTypes';
import { computed, nextTick, onMounted, onUnmounted, PropType, ref, watch } from 'vue';
import { useStore } from 'vuex';
import {
  TriggerConditionAttributeType,
  useTriggerConditions,
} from '@/features/bots/components/designer/Conditions/useTriggerConditions';
import { useAudienceConditions } from '@/features/bots/components/designer/Conditions/useAudienceConditions';

const props = defineProps({
  value: {
    type: String,
    required: true,
  },
  triggerConditions: {
    type: Object as PropType<ConditionGroupViewModel>,
    required: true,
  },
  audienceConditions: {
    type: Object as PropType<ConditionGroupViewModel>,
    required: true,
  },
  schedule: {
    type: Object as PropType<ConditionViewModel>,
  },
  openingTimes: {
    type: Array as PropType<BusinessHoursViewModel[]>,
    default: [],
  },
});

const store = useStore();
const triggerValidity = computed<TriggerValidity>(() => store.getters.triggerValidity);
const isWorkspaceFeatureEnabled = (feature: string, defaultValue = false) =>
  computed(() => store.getters.isWorkspaceFeatureEnabled(feature, defaultValue));
const channelIdentities = computed<ChannelIdentityViewModel[]>(() => store.getters.channelIdentities);
const contactTypes = computed(() => store.getters.targetContactTypes);

const setTrigger = (trigger: string) => store.commit(SET_TRIGGER, trigger);

const updateTriggerConditions = (conditions: ConditionGroupViewModel) =>
  store.commit(UPDATE_TRIGGER_CONDITIONS, conditions);
const updateAudienceConditions = (conditions: ConditionGroupViewModel) =>
  store.commit(UPDATE_AUDIENCE_CONDITIONS, conditions);
const updateScheduleCondition = (condition: ConditionViewModel, openingTimes: BusinessHoursViewModel[]) =>
  store.commit(UPDATE_SCHEDULE_CONDITION, { condition, openingTimes });
const updateContactTypes = (contactTypes: string[]) => store.commit(UPDATE_TARGET_CONTACT_TYPES, contactTypes);

const triggerType = ref('');
const scheduleCondition = ref<ConditionViewModel>({} as ConditionViewModel);
const showConfig = ref(false);
const excludedTriggerTypes = ref<string[]>([]);
const triggerConditionList = ref<ConditionGroupViewModel>({
  type: 'and',
  conditions: [],
});
const audienceConditionList = ref<ConditionGroupViewModel>({
  type: 'and',
  conditions: [],
});

const triggerConditionsManager = useTriggerConditions();
const audienceConditionsManager = useAudienceConditions();

const disableClickAway = ref(false);
const conditionConfigOpen = ref(false);
const isAddNewPickerOpen = ref(false);
const availableTypes = AvailableTriggers.list();
const availableTriggerTypes = computed(() =>
  availableTypes.filter((trigger) => !excludedTriggerTypes.value.includes(trigger.type)),
);

function setConditionOpenedState(isOpen: boolean) {
  conditionConfigOpen.value = isOpen;
}

watch(
  () => props.value,
  (value: string) => (triggerType.value = value),
  { immediate: true },
);
watch(
  () => props.triggerConditions,
  (value: ConditionGroupViewModel) => (triggerConditionList.value = value),
  { immediate: true },
);
watch(
  () => props.audienceConditions,
  (value: ConditionGroupViewModel) => (audienceConditionList.value = value),
  { immediate: true },
);
watch(
  () => props.schedule,
  (value: ConditionViewModel) => (scheduleCondition.value = value),
  { immediate: true },
);

onMounted(() => {
  getExcludedTriggerTypes();
  triggerConditionsManager.excludeConditions(getExcludedConditions());
});

function triggerConditionsUpdated(update: ConditionGroupUpdate) {
  triggerConditionsManager.excludeConditions(getExcludedConditions());
  updateTriggerConditions(update.conditionGroup);
  keepTriggerConfigOpenOnUpdate();
}

function audienceConditionsUpdated(update: ConditionGroupUpdate) {
  triggerConditionsManager.excludeConditions(getExcludedConditions());
  updateAudienceConditions(update.conditionGroup);
  keepTriggerConfigOpenOnUpdate();
}

function contactTypeUpdated(newContactTypes: ContactType[]) {
  updateContactTypes(newContactTypes);
}

function keepTriggerConfigOpenOnUpdate() {
  showTriggerConfig();
  disableClickAway.value = true;
  setTimeout(() => (disableClickAway.value = false), 500); // Hack, clickaway kicks in when some elements selected in conditin pickers
}

function showTriggerConfig() {
  showConfig.value = true;
}

function hideTriggerConfig() {
  if (conditionConfigOpen.value || disableClickAway.value) return;
  showConfig.value = false;
  isAddNewPickerOpen.value = false;
}

function isTriggerSelected(type: TriggerType) {
  return triggerType.value === type;
}

function selectTriggerType(type: TriggerType) {
  handleSwitchingTrigger(type);
  if (triggerType.value === type) {
    triggerType.value = '';
  } else {
    triggerType.value = type;
  }
  setTrigger(triggerType.value);
  triggerConditionsManager.excludeConditions(getExcludedConditions());
}

function onScheduleUpdated(condition: ConditionViewModel | undefined, customTimes: BusinessHoursViewModel[]) {
  updateScheduleCondition(condition!, customTimes);
}

async function getExcludedTriggerTypes() {
  if (!isProactiveFlowsEnabled()) {
    excludedTriggerTypes.value.push(TriggerType.WidgetLoaded);
  }
}

function isProactiveFlowsEnabled() {
  return isWorkspaceFeatureEnabled(PROACTIVE_FLOWS_ENABLED).value;
}

function getExcludedConditions() {
  const excludedConditions: string[] = [];
  if (!isTriggerSelected(TriggerType.MessageReceived)) {
    excludedConditions.push(TriggerConditionAttributeType.MessageText);
    excludedConditions.push(TriggerConditionAttributeType.MessageIntent);
  } else if (!isWorkspaceFeatureEnabled(NLP_ENABLED).value) {
    excludedConditions.push(TriggerConditionAttributeType.MessageIntent);
  } else {
    if (triggerConditionList.value) {
      const messageCondition = ConditionGroup.find(
        triggerConditionList.value,
        (c) => c.attribute === TriggerConditionAttributeType.MessageText,
      );
      if (messageCondition) {
        excludedConditions.push(TriggerConditionAttributeType.MessageIntent);
      }

      const intentCondition = ConditionGroup.find(
        triggerConditionList.value,
        (c) => c.attribute === TriggerConditionAttributeType.MessageIntent,
      );
      if (intentCondition) {
        excludedConditions.push(TriggerConditionAttributeType.MessageText);
        excludedConditions.push(TriggerConditionAttributeType.MessageIntent);
      }
    }
  }
  if (
    isTriggerSelected(TriggerType.WidgetLoaded) ||
    isTriggerSelected(TriggerType.ConversationOpened) ||
    channelIdentities.value.length === 0 ||
    !isWorkspaceFeatureEnabled('multipleIdentitiesPerChannelEnabled')
  ) {
    excludedConditions.push(TriggerConditionAttributeType.ChannelIdentity);
  }

  if (!isTriggerSelected(TriggerType.WidgetLoaded)) {
    excludedConditions.push(TriggerConditionAttributeType.TimeOnPage);
  } else {
    if (triggerConditionList.value) {
      const timeOnPageCondition = ConditionGroup.find(
        triggerConditionList.value,
        (c) => c.attribute === TriggerConditionAttributeType.TimeOnPage,
      );
      if (timeOnPageCondition) {
        excludedConditions.push(TriggerConditionAttributeType.TimeOnPage);
      }
    }
  }
  return excludedConditions;
}

function getTooltipForTrigger(type: TriggerType) {
  if (type === TriggerType.MessageReceived) {
    return `A message is received and the user's intention is recognised`;
  } else if (type === TriggerType.Unknown) {
    return `A message is received and the user's intention is not recognised`;
  } else if (type === TriggerType.ConversationOpened) {
    return `When the user opens the Web Assistant conversation`;
  } else if (type === TriggerType.WidgetLoaded) {
    return `User opens a webpage with Web Assistant installed`;
  }
}

function handleSwitchingTrigger(type: string) {
  const invalidAttributes: string[] = [];
  if (type !== TriggerType.MessageReceived) {
    invalidAttributes.push(TriggerConditionAttributeType.MessageText);
    invalidAttributes.push(TriggerConditionAttributeType.MessageIntent);
  }

  if (type !== TriggerType.WidgetLoaded) {
    invalidAttributes.push(TriggerConditionAttributeType.TimeOnPage);
  }

  if (type !== TriggerType.MessageReceived && type !== TriggerType.Unknown) {
    invalidAttributes.push(TriggerConditionAttributeType.ChannelIdentity);
  }

  if (invalidAttributes.length > 0) {
    triggerConditionList.value = removeInvalidConditions(triggerConditionList.value, invalidAttributes);
    updateTriggerConditions(triggerConditionList.value);
  }
}

function removeInvalidConditions(
  conditionGroup: ConditionGroupViewModel,
  invalidAttributes: string[],
): ConditionGroupViewModel {
  for (let i = conditionGroup.conditions.length - 1; i >= 0; i--) {
    const c = conditionGroup.conditions[i];
    if (ConditionGroup.isCondition(c)) {
      if (invalidAttributes.includes(c.attribute)) {
        conditionGroup.conditions.splice(i, 1);
      }
    } else {
      removeInvalidConditions(c, invalidAttributes);
    }
  }

  return conditionGroup;
}
</script>

<style scoped lang="scss">
.trigger-box-wrapper {
  position: relative;
}

.invalid-warning {
  position: absolute;
  right: -2.5em;
  top: 50%;
  transform: translate(-50%, -50%);
  color: red;
}

.trigger-box-section {
  padding: 1.5em 0.5em 0 0.5em;
  &:first-of-type {
    padding-top: 0.5em;
  }
  &:last-of-type {
    padding-bottom: 0.5em;
  }
}

.trigger-options {
  display: flex;
  margin-bottom: 10px;
}

.trigger-option {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 0.5em;
  flex-basis: 20%;
  cursor: pointer;
  color: $color-netcall-deep-sea;

  &.selected {
    background: #eadc8e;
  }

  &:hover {
    background: #f2ebc5;
  }
}

.trigger-option-icon {
  margin-bottom: 0.5em;
}

.trigger-option-text {
  width: 2em;
  display: flex;
  width: 2em;
  justify-content: center;
}

.field {
  position: relative;
}

.title,
.subtitle {
  word-break: break-word;
}

.subtitle:not(:last-child) {
  margin-bottom: 0.5rem;
}

.icon {
  width: unset;
}

.custom-icon {
  height: 30px;
  width: 30px;
  fill: $color-netcall-deep-sea;
}
</style>
